import { find } from '@execonline-inc/collections';
import { when } from '@execonline-inc/maybe-adapter';
import { noop } from '@kofno/piper';
import { fromNullable, Maybe, nothing } from 'maybeasy';
import * as R from 'ramda';
import { pushState } from '../History';
import { pathname } from '../Location';
import {
  SearchParameter,
  SearchParameters,
  SortDirection,
  UpdatingSearchParameterState,
  UpdatingSortParameterState,
} from './Types';

export const updateUrl = (searchParams: string) =>
  pathname()
    .andThen((pathname) => pushState({}, '', `${pathname}${searchParams}`))
    .fork(() => console.warn('ListFilter:updateUrl -- unable to update URL'), noop);

export const replaceParameter = (
  newSearchParameter: SearchParameter,
  searchParameters: SearchParameters,
) =>
  R.reject((p: SearchParameter) => p.key === newSearchParameter.key, searchParameters).concat([
    { key: newSearchParameter.key, value: newSearchParameter.value },
  ]);

export const valueFor = (key: string, searchParameters: SearchParameters): string =>
  find((parameter: SearchParameter) => parameter.key === key, searchParameters)
    .map((parameter) => parameter.value)
    .getOrElseValue('');

export const fetchParams = (filterName: string, searchParameters?: SearchParameters) => {
  return searchParameters === undefined || searchParameters.length === 0
    ? []
    : searchParameters.map((param) => `${filterName}[${param.key}]=${param.value}`);
};

export const removeLeadingQuestionMark = (parameter: string): string =>
  parameter.charAt(0) === '?' ? parameter.slice(1) : parameter;

const convertParameter = (parameter: string): Maybe<SearchParameter> => {
  const keyValue = parameter.split('=');
  if (keyValue.length !== 2) {
    return nothing();
  }

  return fromNullable(keyValue[0].match(/\[(.*?)]/)).map((k: RegExpMatchArray) => ({
    key: k[1],
    value: keyValue[1],
  }));
};

const addParameterToList = (params: SearchParameter[], parameter: Maybe<SearchParameter>) => {
  return parameter.map((p: SearchParameter) => [...params, p]).getOrElseValue(params);
};

/*
 * On the initial load, the search parameters from the URL are used to pre-populate
 * the search fields for anyone who might have bookmarked the page with
 * criteria in the URL. To do so:
 *     * The first parameter in the query string will have a leading question mark. Strip it.
 *     * Throw out parameters that are not relevant to the workflow items search.
 *     * Convert parameters from the string form of 'workflow_item_filter[status]=1' to
 *       the actual search parameter structure.
 *     * Push each parameter object to the state.
 */
export const getSearchParametersFromUrl = (): SearchParameters => {
  return decodeURI(window.location.search)
    .split('&')
    .map(removeLeadingQuestionMark)
    .map(convertParameter)
    .reduce(addParameterToList, []);
};

export const getClearedParameters = () => {
  updateUrl('');
  return [];
};

export const getUpdatedSearchParameters = (
  state: UpdatingSearchParameterState,
  filterName: string,
): SearchParameters => {
  const searchParameters = replaceParameter(state.newSearchParameter, [...state.searchParameters]);

  updateUrl(`?${fetchParams(filterName, searchParameters).join('&')}`);
  return searchParameters;
};

export const sortDirection = (searchParameters: SearchParameters) =>
  valueFor('sort_direction', searchParameters) === 'desc' ? 'asc' : 'desc';

export const whenSortParamenterChanges = (
  state: UpdatingSortParameterState,
): Maybe<SearchParameters> =>
  when(valueFor('sort_by', state.searchParameters) === state.sortedColumn, state.searchParameters);

export const getUpdatedSortParameters = (
  state: UpdatingSortParameterState,
  filterName: string,
): SearchParameters => {
  const newSortDirection: SortDirection = whenSortParamenterChanges(state)
    .map(sortDirection)
    .getOrElseValue('asc');

  const sortByParam = { key: 'sort_by', value: state.sortedColumn };
  const sortDirectionParam = { key: 'sort_direction', value: newSortDirection };

  const searchParameters = replaceParameter(
    sortByParam,
    replaceParameter(sortDirectionParam, [...state.searchParameters]),
  );
  updateUrl(`?${fetchParams(filterName, searchParameters).join('&')}`);
  return searchParameters;
};
